import React from 'react';
import { createPortal } from 'react-dom';
import { Share2, MessageCircle, Mail, Link as LinkIcon, Twitter, Facebook, Linkedin, Instagram } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import {
  generateShareUrl,
  generateShareText,
  buildRichCaption,
  canUseNativeShare,
  getWhatsAppShareUrl,
  getEmailShareUrl,
  getTwitterShareUrl,
  getFacebookShareUrl,
  getLinkedInShareUrl,
  getInstagramShareText,
  copyToClipboard,
  ShareData,
  ShareChannel,
} from '@/utils/shareUtils';

interface ShareListingButtonProps {
  listingId: string;
  title: string;
  price: number;
  location?: string;
  listingType: string;
  assetType?: 'helicopter' | 'engine' | 'parts' | 'jobs' | 'services';
  variant?: 'outline' | 'ghost' | 'secondary';
  size?: 'sm' | 'default' | 'icon';
  className?: string;
  showLabel?: boolean;
  source?: 'listing_card' | 'detail_modal' | 'share_page';
}

export const ShareListingButton: React.FC<ShareListingButtonProps> = ({
  listingId,
  title,
  price,
  location,
  listingType,
  assetType = 'helicopter',
  variant = 'ghost',
  size = 'icon',
  className = '',
  showLabel = false,
  source = 'detail_modal',
}) => {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = React.useState({ top: 0, right: 16 });
  const shareData: ShareData = { listingId, title, price, location, listingType, assetType };

  const positionMenu = React.useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setMenuPosition({
      top: rect.bottom + 6,
      right: Math.max(8, window.innerWidth - rect.right),
    });
  }, []);

  React.useEffect(() => {
    if (!open) return;
    positionMenu();
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    window.addEventListener('resize', positionMenu);
    window.addEventListener('scroll', positionMenu, true);
    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      window.removeEventListener('resize', positionMenu);
      window.removeEventListener('scroll', positionMenu, true);
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [open, positionMenu]);

  const urlFor = (channel: ShareChannel) => generateShareUrl(listingId, assetType, channel);

  const logShare = (channel: ShareChannel) => {
    // Fire-and-forget. RLS allows anon insert.
    supabase
      .from('listing_share_events')
      .insert({
        listing_id: listingId,
        asset_type: assetType,
        channel,
        source,
        utm: { utm_source: channel, utm_medium: 'social', utm_campaign: 'listing_share' },
      })
      .then(({ error }) => {
        if (error) console.warn('[ShareListingButton] log failed:', error.message);
      });
    import('@/lib/analytics/track').then(({ trackEvent }) =>
      trackEvent({
        event_name: 'listing_share',
        event_category: 'share',
        asset_type: assetType,
        asset_id: listingId,
        properties: { channel, source },
      })
    );
  };

  const handleNativeShare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const shareUrl = urlFor('native');
    if (canUseNativeShare()) {
      try {
        await navigator.share({ title, text: generateShareText(shareData), url: shareUrl });
        logShare('native');
        toast({ title: 'Shared successfully!', description: 'The listing has been shared.' });
      } catch {
        // user cancelled
      }
    }
  };

  const handleWhatsAppShare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const shareUrl = urlFor('whatsapp');
    const url = getWhatsAppShareUrl(generateShareText(shareData), shareUrl);
    const opened = window.open(url, '_blank', 'noopener,noreferrer');
    if (!opened) await copyToClipboard(`${generateShareText(shareData)}\n${shareUrl}`);
    logShare('whatsapp');
  };

  const handleEmailShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    const shareUrl = urlFor('email');
    window.location.href = getEmailShareUrl(title, generateShareText(shareData), shareUrl);
    logShare('email');
  };

  const handleCopyLink = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const shareUrl = urlFor('copy');
    const success = await copyToClipboard(shareUrl);
    if (success) {
      toast({ title: 'Link copied!', description: 'The listing link has been copied to your clipboard.' });
      logShare('copy');
    }
  };

  const handleTwitterShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    const shareUrl = urlFor('x');
    window.open(getTwitterShareUrl(generateShareText(shareData), shareUrl), '_blank', 'width=550,height=420');
    logShare('x');
  };

  const handleFacebookShare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const shareUrl = urlFor('facebook');
    // Facebook's sharer.php strips text — copy a rich caption to clipboard
    // so the user can paste it into the composer.
    await copyToClipboard(buildRichCaption(shareData, shareUrl));
    toast({
      title: 'Caption copied',
      description: 'Paste it into the Facebook composer (Cmd/Ctrl+V).',
    });
    window.open(getFacebookShareUrl(shareUrl), '_blank', 'width=550,height=420');
    logShare('facebook');
  };

  const handleLinkedInShare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const shareUrl = urlFor('linkedin');
    // LinkedIn share-offsite ignores any prefilled text — copy caption first.
    await copyToClipboard(buildRichCaption(shareData, shareUrl));
    toast({
      title: 'Caption copied',
      description: 'Paste it into the LinkedIn post (Cmd/Ctrl+V).',
    });
    window.open(getLinkedInShareUrl(shareUrl, title), '_blank', 'width=550,height=420');
    logShare('linkedin');
  };

  const handleInstagramShare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const shareUrl = urlFor('instagram');
    const success = await copyToClipboard(getInstagramShareText(generateShareText(shareData), shareUrl));
    if (success) {
      toast({
        title: 'Copied for Instagram!',
        description: 'Text & link copied. Open Instagram and paste in your post or story.',
      });
      logShare('instagram');
    }
  };

  const menu = open && typeof document !== 'undefined' ? createPortal(
    <div
      ref={menuRef}
      role="menu"
      className="fixed z-[200] w-48 overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
      style={{ top: menuPosition.top, right: menuPosition.right }}
      onClick={(e) => e.stopPropagation()}
    >
      {canUseNativeShare() && (
        <button type="button" role="menuitem" className="relative flex w-full select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground" onClick={handleNativeShare}>
          <Share2 className="mr-2 h-4 w-4" />
          Share...
        </button>
      )}
      <button type="button" role="menuitem" className="relative flex w-full select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground" onClick={handleWhatsAppShare}>
        <MessageCircle className="mr-2 h-4 w-4" />
        WhatsApp
      </button>
      <button type="button" role="menuitem" className="relative flex w-full select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground" onClick={handleInstagramShare}>
        <Instagram className="mr-2 h-4 w-4" />
        Instagram
      </button>
      <button type="button" role="menuitem" className="relative flex w-full select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground" onClick={handleFacebookShare}>
        <Facebook className="mr-2 h-4 w-4" />
        Facebook
      </button>
      <button type="button" role="menuitem" className="relative flex w-full select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground" onClick={handleTwitterShare}>
        <Twitter className="mr-2 h-4 w-4" />
        Twitter / X
      </button>
      <button type="button" role="menuitem" className="relative flex w-full select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground" onClick={handleLinkedInShare}>
        <Linkedin className="mr-2 h-4 w-4" />
        LinkedIn
      </button>
      <button type="button" role="menuitem" className="relative flex w-full select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground" onClick={handleEmailShare}>
        <Mail className="mr-2 h-4 w-4" />
        Email
      </button>
      <button type="button" role="menuitem" className="relative flex w-full select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground" onClick={handleCopyLink}>
        <LinkIcon className="mr-2 h-4 w-4" />
        Copy Link
      </button>
    </div>,
    document.body,
  ) : null;

  // Stop click bubbling so parent cards/modals don't intercept the action.
  return (
    <span onClick={(e) => e.stopPropagation()} className="inline-flex">
      <Button
        ref={triggerRef}
        variant={variant}
        size={size}
        className={className}
        aria-label="Share listing"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          positionMenu();
          setOpen((current) => !current);
        }}
      >
        <Share2 className="h-4 w-4" />
        {showLabel && <span className="ml-2">Share</span>}
      </Button>
      {menu}
    </span>
  );
};
